import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2, Save, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFAQs = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyFaq = {
    category: 'Genel',
    question: '',
    answer: '',
    order: 0,
    is_active: true
  };
  
  const [formData, setFormData] = useState(emptyFaq);

  const categories = [
    'Eşya Satış Süreci',
    'Satın Alınan Eşyalar',
    'Ödeme ve Fiyatlandırma',
    'WhatsApp ve İletişim',
    'Hizmet Bölgeleri',
    'Genel'
  ];

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faqs`);
      setFaqs(response.data);
    } catch (error) {
      toast.error('SSS yüklenemedi');
    }
    setLoading(false);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData(faq);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingFaq(null);
    setFormData({ ...emptyFaq, order: faqs.length });
    setIsDialogOpen(true);
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Bu SSS\'yi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/faqs/${faqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SSS silindi');
      fetchFaqs();
    } catch (error) {
      toast.error('SSS silinemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingFaq) {
        await axios.put(`${API_URL}/api/faqs/${editingFaq.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SSS güncellendi');
      } else {
        await axios.post(`${API_URL}/api/faqs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SSS oluşturuldu');
      }
      setIsDialogOpen(false);
      fetchFaqs();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
    setSaving(false);
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'Genel';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-faqs">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sık Sorulan Sorular</h1>
          <p className="text-slate-600">SSS bölümünü yönetin.</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="add-faq-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Soru
        </Button>
      </div>

      {faqs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-600">
            Henüz SSS eklenmemiş.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category}>
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#002FA7]" />
                {category}
              </h2>
              <div className="space-y-2">
                {categoryFaqs.map((faq, index) => (
                  <Card key={faq.id} data-testid={`faq-item-${index}`}>
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-900">{faq.question}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mt-1">{faq.answer}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(faq)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(faq.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'SSS Düzenle' : 'Yeni SSS'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Kategori</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border rounded-md p-2"
                  data-testid="faq-category-select"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="order">Sıra</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="question">Soru</Label>
              <Input
                id="question"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                required
                data-testid="faq-question-input"
              />
            </div>

            <div>
              <Label htmlFor="answer">Cevap</Label>
              <Textarea
                id="answer"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                rows={6}
                required
                data-testid="faq-answer-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#001d6e]" data-testid="save-faq-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFAQs;
